import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { CourseOption, CourseSummary } from "../types/course.response";
import { getPoolSuggestions } from "../services/poolSuggestions";
import {
    ensureHydrated,
    getCatalogEntry,
    getCatalogVersion,
    subscribeCatalog,
} from "../services/courseCatalog";
import { INITIAL_USER_STATE } from "../utils/fulfillmentMerge";
import { toCourseSummary } from "../utils/programData";
import { SuggestionsStatus } from "./useSuggestedCourses";

/**
 * Pool 的推荐课:pools/:poolKey/suggestions 拿 courseIds(会话级缓存),
 * 经 /courses/batch 补水后合成"未在用户课表"初始态的 CourseSummary 行。
 * 后端已排除请求时刻的用户已有课;会话内新增的课按最新 userCourses
 * 在这里再过滤(含 topic 变体,按课号比)。
 * courseRequirements:课号 → 挂靠 requirement ids,供行下方的归属提示。
 */
export function usePoolSuggestedCourses(
    programId: string,
    poolKey: string,
    userCourses?: CourseOption[],
): { status: SuggestionsStatus; courses: CourseSummary[]; courseRequirements: Record<string, string[]> } {
    const [status, setStatus] = useState<SuggestionsStatus>("loading");
    const [courseIds, setCourseIds] = useState<string[]>([]);
    const [courseRequirements, setCourseRequirements] = useState<Record<string, string[]>>({});
    const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);

    useEffect(() => {
        let cancelled = false;
        setStatus("loading");
        setCourseIds([]);
        setCourseRequirements({});
        getPoolSuggestions(programId, poolKey)
            .then(async ({ courseIds: ids, courseRequirements: reqs }) => {
                if (ids.length > 0) await ensureHydrated(ids);
                if (cancelled) return;
                setCourseIds(ids);
                setCourseRequirements(reqs);
                setStatus("loaded");
            })
            .catch(err => {
                console.error(
                    `[usePoolSuggestedCourses] failed (program=${programId}, pool=${poolKey})`,
                    err,
                );
                if (!cancelled) setStatus("error");
            });
        return () => { cancelled = true; };
    }, [programId, poolKey]);

    const courses = useMemo(() => {
        void catalogVersion; // catalog 缓存变化时重新合成
        if (courseIds.length === 0) return [];
        const ownedIds = new Set((userCourses ?? []).map(c => c.id));
        const result: CourseSummary[] = [];
        for (const id of courseIds) {
            if (ownedIds.has(id)) continue;
            const catalog = getCatalogEntry(id);
            if (!catalog) continue; // 补水未到/被清,该行先不出
            result.push(
                toCourseSummary({ ...catalog, userItemId: null, userState: INITIAL_USER_STATE }, id),
            );
        }
        return result;
    }, [courseIds, userCourses, catalogVersion]);

    return { status, courses, courseRequirements };
}
